import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { actions } from "./appSlice";
import { Provider, useDispatch, useSelector } from "react-redux";
import publicRoutes from "../routes/publicRoutes/publicRoutes";
import ErrorPage from "../components/ErrorPage/ErrorPage";
import { ProtectedLayout } from "../components/ProtectedLayout/ProtectedLayout";
import { PublicLayout } from "../components/PublicLayout/PublicLayout";
import CommonPage from "../components/ProtectedLayout/CommonPage/CommonPage";
import { generateDynamicProtectedRoutes } from "../routes/protectedRoutes/generateDynamicProtectedRoutes";
import { ROLE, SUB_ROLE } from "../utils/constants/profiles";
import { startSignalRConnection, stopSignalRConnection } from "../signalR";
import { store } from "./store/store";
import { useNotificationListQuery } from "../features/api/user/notificationUserApi";
import { useNotificationAdminListQuery } from "../features/api/admin/notificationAdmin";
import { t } from "i18next";

const App = () => {
  const dispatch = useDispatch();
  const appConfig = useSelector((state) => state.app.appConfig);
  const employment = useSelector(
    (state) => state.app.employmentTypeLanguageKey
  );
  const studioId = useSelector((state) => state.app.studioId);
  const primaryColor = useSelector((state) => state.app.primaryColor);
  const adminId = useSelector((state) => state.app.adminId);
  const role = useSelector((state) => state.app.role);
  const [isConfigLoading, setIsConfigLoading] = useState(true);

  const {
    data: listNotificationUser,
    error: userError,
    refetch: refetchUserNotifications,
  } = useNotificationListQuery(undefined, {
    skip: role !== ROLE.USER,
  });

  const {
    data: listNotificationAdmin,
    error: adminError,
    refetch: refetchAdminNotifications,
  } = useNotificationAdminListQuery(undefined, {
    skip: !(role === ROLE.ADMIN || role === ROLE.BUSINESS),
  });

  useEffect(() => {
    if (userError && role === ROLE.USER) {
      const timeoutId = setTimeout(() => {
        refetchUserNotifications();
        window.location.reload();
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
    if (adminError && role !== ROLE.USER) {
      const timeoutId = setTimeout(() => {
        refetchAdminNotifications();
        window.location.reload();
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [
    userError,
    adminError,
    refetchUserNotifications,
    refetchAdminNotifications,
    role,
  ]);

  useEffect(() => {
    if (listNotificationUser && role === ROLE.USER) {
      dispatch(actions.clearNotifications());
      listNotificationUser?.Data?.items.forEach((notification) => {
        dispatch(actions.addNotification(notification));
      });
    }
    if (listNotificationAdmin && role !== ROLE.USER) {
      dispatch(actions.clearNotifications());
      listNotificationAdmin?.Data?.items.forEach((notification) => {
        dispatch(actions.addNotification(notification));
      });
    }
  }, [listNotificationUser, listNotificationAdmin, dispatch, role]);

  useEffect(() => {
    startSignalRConnection(adminId, employment, studioId);
    return () => {
      stopSignalRConnection();
    };
  }, [adminId, employment, studioId]);

  useEffect(() => {
    fetch("/app-config.json", {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      cache: "no-store",
    })
      .then(async (res) => {
        const config = await res.json();
        dispatch(actions.setConfig(config));
        !primaryColor &&
          dispatch(
            actions.setColors({
              primaryColor: config.assets.colors.primaryColor,
              secondaryColor: config.assets.colors.secondaryColor,
            })
          );
        setIsConfigLoading(false);
      })
      .catch(() => toast.error(t("ReqErr")));
  }, [dispatch, primaryColor]);

  useEffect(() => {
    if (!appConfig) {
      const timeoutId = setTimeout(() => {
        window.location.reload();
      }, 1000);
      return clearTimeout(timeoutId);
    }
  }, [appConfig]);

  if (isConfigLoading) return <CircularProgress />;
  else
    return (
      <Provider store={store}>
        <div id="app" className="app">
          <RouterProvider
            router={createBrowserRouter([
              {
                path: "/",
                element: <PublicLayout />,
                children: publicRoutes,
                errorElement: <ErrorPage />,
              },
              {
                path: "/auth",
                element: <ProtectedLayout />,
                children: [
                  {
                    path: "admin", // IBC
                    element: <CommonPage enabledRole={ROLE.ADMIN} />,
                    children: generateDynamicProtectedRoutes(
                      ROLE.ADMIN,
                      appConfig
                    ),
                  },
                  {
                    path: "business", // GESTORE, RESPONSABILE STUDIO + OPERATORI STUDIO
                    element: <CommonPage enabledRole={ROLE.BUSINESS} />,
                    children: generateDynamicProtectedRoutes(
                      ROLE.BUSINESS,
                      appConfig
                    ),
                  },
                  {
                    path: "operator", // COMMERCIALISTA OPERATORE
                    element: <CommonPage enabledRole={SUB_ROLE.OPERATOR} />,
                    children: generateDynamicProtectedRoutes(
                      SUB_ROLE.OPERATOR,
                      appConfig
                    ),
                  },
                  {
                    path: "user", // CLIENTI STUDIO
                    element: <CommonPage enabledRole={ROLE.USER} />,
                    children: generateDynamicProtectedRoutes(
                      ROLE.USER,
                      appConfig
                    ),
                  },
                ],
              },
              {
                path: "/error",
                element: <ErrorPage />,
              },
            ])}
          />
          <ToastContainer
            position="top-right"
            autoClose={4000}
            theme="colored"
            closeOnClick
            newestOnTop
            limit={3}
          />
        </div>
      </Provider>
    );
};

export default App;
